// API client — thin fetch wrapper that always sends credentials so the
// session cookie travels cross-subdomain (app.* -> api.*). Throws ApiError
// with .status for handlers to branch on (401 -> redirect to /login etc.).
//
// We deliberately do NOT layer a request-cache or react-query here in
// v0.1.0. The dashboard surface is tiny; one useEffect per page is fine.
// Add TanStack Query when the screens get complex.

export const API_BASE_URL = __API_BASE_URL__;

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

type ReqInit = Omit<RequestInit, "body"> & { body?: unknown };

export async function api<T = unknown>(path: string, init: ReqInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
    body: init.body == null ? null : JSON.stringify(init.body),
  });
  // 204 No Content -> resolve to null without trying to parse.
  if (res.status === 204) return null as T;
  const text = await res.text();
  const json = text ? safeParse(text) : null;
  if (!res.ok) {
    const msg = isErrorBody(json) ? json.error : `HTTP ${res.status}`;
    throw new ApiError(res.status, json, msg);
  }
  return json as T;
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}
function isErrorBody(x: unknown): x is { error: string } {
  return typeof x === "object" && x !== null && typeof (x as { error?: unknown }).error === "string";
}

// apiFormData posts a multipart/form-data body. We don't JSON-encode and we
// let the browser set Content-Type so the boundary is correct.
export async function apiFormData<T = unknown>(
  path: string,
  body: FormData,
  init: Omit<RequestInit, "body" | "method"> = {},
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    method: "POST",
    credentials: "include",
    body,
  });
  if (res.status === 204) return null as T;
  const text = await res.text();
  const json = text ? safeParse(text) : null;
  if (!res.ok) {
    const msg = isErrorBody(json) ? json.error : `HTTP ${res.status}`;
    throw new ApiError(res.status, json, msg);
  }
  return json as T;
}

// assetURL maps the relative proxy path returned by brand-service to an
// absolute URL the browser can fetch. Use this everywhere the SPA renders
// or links to an asset.
//
// v0.5.20 (2026-05-21) — legacy URL rewrite. Older
// generation_versions rows (pre-v0.2.34) stored image_url as the
// auth-required proxy path:
//   /v1/brands/{brand_id}/assets/{asset_id}/data
// The newer publishable-asset endpoint:
//   /v1/public/assets/{asset_id}
// ...is anonymously fetchable. Cross-origin <img> requests don't
// carry cookies, so the legacy path 401s when the dashboard loads
// it. Rewrite at render time so old + new generations both work.
//
// Only safe for asset kinds that the brand-service `IsPublishable`
// check allows on /v1/public/assets/{id} — kind=generated (the case
// for every gallery item) qualifies. If it doesn't, the rewrite
// still returns a 404 from /v1/public, which the dashboard handles
// via the <img onError> fallback we added with v0.5.20.
const LEGACY_ASSET_PATH = /^\/v1\/brands\/[0-9a-fA-F-]+\/assets\/([0-9a-fA-F-]+)\/data$/;
export function assetURL(path: string): string {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const m = LEGACY_ASSET_PATH.exec(path);
  if (m) {
    return `${API_BASE_URL}/v1/public/assets/${m[1]}`;
  }
  return `${API_BASE_URL}${path}`;
}

// ---------- typed wrappers for the endpoints we use today ----------

export type User = {
  id: string;
  email: string;
  display_name: string;
  locale: string;
  timezone: string;
  created_at: string;
};

export type Workspace = {
  id: string;
  name: string;
  slug: string;
  owner_user_id: string;
  timezone: string;
  locale: string;
  status: string;
  created_at: string;
};

export type MeResponse = {
  user: User;
  workspaces: Workspace[];
  current_workspace_id?: string;
};

export const auth = {
  register: (body: { email: string; password: string; display_name: string; locale?: string; timezone?: string }) =>
    api<User>("/v1/auth/register", { method: "POST", body }),
  login: (body: { email: string; password: string }) =>
    api<User>("/v1/auth/login", { method: "POST", body }),
  logout: () => api<null>("/v1/auth/logout", { method: "POST" }),
  me: () => api<MeResponse>("/v1/auth/me", { method: "GET" }),
  patchMe: (body: { display_name?: string; locale?: string; timezone?: string; avatar_url?: string }) =>
    api<User>("/v1/auth/me", { method: "PATCH", body }),

  // v0.3.3 — email verification + password reset.
  verifyEmail: (token: string) =>
    api<{ status: string; email: string }>(`/v1/auth/verify-email?token=${encodeURIComponent(token)}`, { method: "GET" }),
  resendVerification: () =>
    api<{ status: string }>("/v1/auth/resend-verification", { method: "POST" }),
  forgotPassword: (body: { email: string }) =>
    api<{ status: string }>("/v1/auth/forgot-password", { method: "POST", body }),
  resetPassword: (body: { token: string; new_password: string }) =>
    api<{ status: string }>("/v1/auth/reset-password", { method: "POST", body }),
};

export type GeoHint = { country: string; suggested_locale: string };

export const geo = {
  get: () => api<GeoHint>("/v1/geo", { method: "GET" }),
};

// ---------- brand-service ----------

export type Brand = {
  id: string;
  workspace_id: string;
  legal_name: string;
  trade_name: string;
  slug: string;
  country: string;
  primary_language: string;
  timezone: string;
  industry?: string | null;
  category?: string | null;
  ai_disclosure: boolean;
  comments_mode: "approval" | "auto";
  include_holiday_posts: boolean;
  /** Telegram channel (e.g. "@brandname") for shared_bot publishing.
   *  null/undefined ⇒ not configured. v0.1.x interim — moves to
   *  social.social_accounts when nexus-social-service ships. */
  telegram_channel?: string | null;
  status: string;
  trademark_info?: unknown;
  created_at: string;
  updated_at: string;
};

// Image engine identifiers. Mirror of imageEngineRoster in
// nexus-content-service/internal/handlers/image_engine_roster.go and
// the CHECK constraint in nexus-brand-service migration 006. The
// roster order here is also the rotation order for reject+regenerate.
export const IMAGE_ENGINES = [
  "claude-html-opus",
  "fal-recraft-v3",
  "fal-ideogram-v3",
  "claude-html-sonnet",
  "fal-flux-pro",
] as const;
export type ImageEngine = (typeof IMAGE_ENGINES)[number];

export type BrandKit = {
  brand_id: string;
  source: "uploaded" | "generated" | "hybrid";
  palette: Array<{ name?: string; hex: string; role?: string }>;
  typography: Record<string, unknown>;
  logo_asset_id?: string | null;
  guidelines_doc_id?: string | null;
  voice_summary?: string | null;
  // Operator-pinned starting engine for image generation on this
  // brand. null/missing = auto-rotate from the roster default
  // (claude-html-opus first, then through the roster on regenerate).
  ai_image_engine?: ImageEngine | null;
  created_at: string;
  updated_at: string;
};

export type BrandKB = {
  brand_id: string;
  business_description?: string | null;
  key_differentiators: string[];
  raw_prompt?: string | null;
  raw_prompt_lang?: string | null;
  classification_run_id?: string | null;
  classified_at?: string | null;
  status: "pending" | "classifying" | "clarifying" | "ready" | "failed";
  completeness_score?: number | null;
  updated_at: string;
};

// Brand Studio Phase 2 — sub-resource types.

export type VoiceAttribute = {
  brand_id: string;
  attribute: string;
  value: number; // -1..+1 slider
  source: "user" | "classifier" | "learning";
  updated_at: string;
};

export type BrandAudience = {
  brand_id: string;
  demographics: Record<string, unknown>;
  psychographics: Record<string, unknown>;
  updated_at: string;
};

export type BrandProduct = {
  id: string;
  brand_id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  features: string[];
  order_index: number;
  created_at: string;
};

export type BrandService = {
  id: string;
  brand_id: string;
  name: string;
  description?: string | null;
  target_audience?: string | null;
  price_hint?: string | null;
  order_index: number;
  created_at: string;
};

export type BrandTopic = {
  id: string;
  brand_id: string;
  topic: string;
  mode: "include" | "avoid";
  source: string;
  created_at: string;
};

// Brand post templates (v0.3.4) — reusable post skeletons. AI pipeline
// picks a matching one when generating content for the brand. See
// migrations/003_brand_post_templates.sql and docs/05-api.md §6.x.

export type TemplatePlatform =
  | "instagram"
  | "facebook"
  | "linkedin"
  | "x"
  | "telegram"
  | "tiktok"
  | "any";

export type TemplateContentType =
  | "post"
  | "story"
  | "reel"
  | "carousel"
  | "dm"
  | "any";

export type BrandPostTemplateVar = {
  name: string;
  kind: "text" | "number" | "date" | "url" | "audience";
  example?: string;
};

export type BrandPostTemplate = {
  id: string;
  brand_id: string;
  workspace_id: string;
  name: string;
  platform: TemplatePlatform;
  content_type: TemplateContentType;
  body: string;
  vars: BrandPostTemplateVar[];
  image_asset_id?: string | null;
  audience_kind: string;
  language: string;
  tone?: string | null;
  is_enabled: boolean;
  use_count: number;
  last_used_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type BrandPostTemplateInput = {
  name: string;
  platform: TemplatePlatform;
  content_type?: TemplateContentType;
  body: string;
  vars?: BrandPostTemplateVar[];
  image_asset_id?: string | null;
  audience_kind?: string;
  language?: string;
  tone?: string | null;
  is_enabled?: boolean;
};

// Social accounts (v0.4.0 — docs/05-api §6)

export type SocialPlatform = "facebook" | "instagram" | "linkedin" | "x" | "tiktok";
export type SocialConnectMode = "oauth" | "bot_token" | "shared_bot";
export type SocialAccountStatus = "active" | "reauth_required" | "disconnected" | "suspended";

export type SocialAccount = {
  id: string;
  workspace_id: string;
  brand_id: string;
  platform: SocialPlatform;
  platform_account_id: string;
  display_name: string;
  username?: string | null;
  connect_mode: SocialConnectMode;
  connected_by: string;
  connected_at: string;
  account_created_at?: string | null;
  status: SocialAccountStatus;
  metadata?: Record<string, unknown>;
  disconnected_at?: string | null;
};

export type BrandAssetKind =
  | "logo"
  | "product"
  | "team"
  | "background"
  | "example"
  | "document"
  | "other"
  // "generated" — uploaded by content-service after a successful
  // Claude-HTML composition (PNG rendered via chromedp). Added in
  // brand-service v0.5.9 (matching content-service v0.2.24+).
  | "generated";

export type BrandAsset = {
  id: string;
  brand_id: string;
  workspace_id: string;
  kind: BrandAssetKind;
  filename_original: string;
  filename_stored: string;
  mime_type: string;
  size_bytes: number;
  width_px?: number | null;
  height_px?: number | null;
  bucket: string;
  storage_key: string;
  checksum_sha256: string;
  exif_stripped: boolean;
  reencoded: boolean;
  uploaded_by: string;
  uploaded_at: string;
  deleted_at?: string | null;
  tags: string[];
  url: string;            // proxy path: /v1/brands/{id}/assets/{aid}/data
  presigned_url?: string; // internal use only; SPA should prefer `url`
};

export const brands = {
  list: () => api<{ brands: Brand[] }>("/v1/brands", { method: "GET" }),
  get: (id: string) => api<Brand>(`/v1/brands/${id}`, { method: "GET" }),
  create: (body: {
    legal_name: string;
    trade_name: string;
    slug: string;
    country: string;
    primary_language: string;
    timezone: string;
    industry?: string;
    category?: string;
    ai_disclosure?: boolean;
    comments_mode?: "approval" | "auto";
    include_holiday_posts?: boolean;
  }) => api<Brand>("/v1/brands", { method: "POST", body }),
  patch: (
    id: string,
    body: Partial<{
      legal_name: string;
      trade_name: string;
      slug: string;
      country: string;
      primary_language: string;
      timezone: string;
      industry: string;
      category: string;
      ai_disclosure: boolean;
      comments_mode: "approval" | "auto";
      include_holiday_posts: boolean;
      /** Pass "" to clear; pass "@channel" or numeric id to set. */
      telegram_channel: string;
    }>,
  ) => api<Brand>(`/v1/brands/${id}`, { method: "PATCH", body }),
  delete: (id: string) => api<null>(`/v1/brands/${id}`, { method: "DELETE" }),
  getKit: (id: string) => api<BrandKit>(`/v1/brands/${id}/kit`, { method: "GET" }),
  upsertKit: (id: string, body: Partial<BrandKit> & { source: BrandKit["source"] }) =>
    api<BrandKit>(`/v1/brands/${id}/kit`, { method: "PUT", body }),
  getKB: (id: string) => api<BrandKB>(`/v1/brands/${id}/kb`, { method: "GET" }),
  upsertKB: (
    id: string,
    body: Partial<{
      business_description: string;
      key_differentiators: string[];
      raw_prompt: string;
      raw_prompt_lang: string;
      status: BrandKB["status"];
    }>,
  ) => api<BrandKB>(`/v1/brands/${id}/kb`, { method: "PUT", body }),

  // Voice
  listVoice: (id: string) =>
    api<{ attributes: VoiceAttribute[] }>(`/v1/brands/${id}/voice`, { method: "GET" }),
  replaceVoice: (
    id: string,
    attributes: Array<{ attribute: string; value: number; source?: "user" | "classifier" | "learning" }>,
  ) =>
    api<{ attributes: VoiceAttribute[] }>(`/v1/brands/${id}/voice`, {
      method: "PUT",
      body: { attributes },
    }),

  // Audience
  getAudience: (id: string) =>
    api<BrandAudience>(`/v1/brands/${id}/audience`, { method: "GET" }),
  upsertAudience: (
    id: string,
    body: Partial<{ demographics: Record<string, unknown>; psychographics: Record<string, unknown> }>,
  ) => api<BrandAudience>(`/v1/brands/${id}/audience`, { method: "PUT", body }),

  // Products
  listProducts: (id: string) =>
    api<{ products: BrandProduct[] }>(`/v1/brands/${id}/products`, { method: "GET" }),
  createProduct: (
    id: string,
    body: { name: string; description?: string; category?: string; features?: string[]; order_index?: number },
  ) => api<BrandProduct>(`/v1/brands/${id}/products`, { method: "POST", body }),
  patchProduct: (
    id: string,
    pid: string,
    body: { name: string; description?: string; category?: string; features?: string[]; order_index?: number },
  ) => api<BrandProduct>(`/v1/brands/${id}/products/${pid}`, { method: "PATCH", body }),
  deleteProduct: (id: string, pid: string) =>
    api<null>(`/v1/brands/${id}/products/${pid}`, { method: "DELETE" }),

  // Services
  listServices: (id: string) =>
    api<{ services: BrandService[] }>(`/v1/brands/${id}/services`, { method: "GET" }),
  createService: (
    id: string,
    body: { name: string; description?: string; target_audience?: string; price_hint?: string; order_index?: number },
  ) => api<BrandService>(`/v1/brands/${id}/services`, { method: "POST", body }),
  patchService: (
    id: string,
    sid: string,
    body: { name: string; description?: string; target_audience?: string; price_hint?: string; order_index?: number },
  ) => api<BrandService>(`/v1/brands/${id}/services/${sid}`, { method: "PATCH", body }),
  deleteService: (id: string, sid: string) =>
    api<null>(`/v1/brands/${id}/services/${sid}`, { method: "DELETE" }),

  // Topics
  listTopics: (id: string) =>
    api<{ topics: BrandTopic[] }>(`/v1/brands/${id}/topics`, { method: "GET" }),
  createTopic: (id: string, body: { topic: string; mode: "include" | "avoid" }) =>
    api<BrandTopic>(`/v1/brands/${id}/topics`, { method: "POST", body }),
  deleteTopic: (id: string, tid: string) =>
    api<null>(`/v1/brands/${id}/topics/${tid}`, { method: "DELETE" }),

  // Assets (v0.3.0)
  listAssets: (id: string, kind?: BrandAssetKind) => {
    const qs = kind ? `?kind=${encodeURIComponent(kind)}` : "";
    return api<{ assets: BrandAsset[] }>(`/v1/brands/${id}/assets${qs}`, { method: "GET" });
  },
  uploadAsset: (
    id: string,
    file: File,
    opts: { kind?: BrandAssetKind; tags?: string[] } = {},
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", opts.kind ?? "example");
    if (opts.tags && opts.tags.length > 0) {
      fd.append("tags", opts.tags.join(","));
    }
    return apiFormData<BrandAsset>(`/v1/brands/${id}/assets`, fd);
  },
  deleteAsset: (id: string, aid: string) =>
    api<null>(`/v1/brands/${id}/assets/${aid}`, { method: "DELETE" }),

  // Social accounts (v0.4.0) — OAuth + bot-token connections.
  listSocialAccounts: (id: string) =>
    api<{ social_accounts: SocialAccount[] }>(`/v1/brands/${id}/social-accounts`, { method: "GET" }),
  startMetaOAuth: (id: string) =>
    api<{ authorize_url: string; state: string }>(
      `/v1/brands/${id}/social-accounts/meta/oauth/start`,
      { method: "POST" },
    ),
  // v0.5.27 — Meta page picker. After the OAuth callback, the dashboard
  // is redirected with ?meta_pick=<state>. We fetch the granted pages,
  // show the operator a checkbox picker, then POST the selection.
  // Backend inserts ONLY the chosen pages → prevents cross-brand leak.
  getMetaPendingPages: (id: string, state: string) =>
    api<{ pages: Array<{
      id: string;
      name: string;
      ig_account_id?: string;
      ig_username?: string;
      ig_name?: string;
    }> }>(`/v1/brands/${id}/social-accounts/meta/pending/${encodeURIComponent(state)}`, {
      method: "GET",
    }),
  selectMetaPages: (id: string, body: { state: string; selected_page_ids: string[] }) =>
    api<{ connected: number }>(`/v1/brands/${id}/social-accounts/meta/select`, {
      method: "POST",
      body,
    }),
  /** Start LinkedIn OAuth (Sign In + Share on LinkedIn scopes; personal
   *  profile only until MDP-approved Community Management API). */
  startLinkedInOAuth: (id: string) =>
    api<{ authorize_url: string; state: string }>(
      `/v1/brands/${id}/social-accounts/linkedin/oauth/start`,
      { method: "POST" },
    ),
  /** Start X (Twitter) OAuth 2.0 with PKCE. Backend returns 503 if
   *  X_ENABLED env flag is off on brand-service — dashboard can use
   *  that to render a "coming soon" state without a 404. */
  startXOAuth: (id: string) =>
    api<{ authorize_url: string; state: string }>(
      `/v1/brands/${id}/social-accounts/x/oauth/start`,
      { method: "POST" },
    ),
  disconnectSocialAccount: (id: string, accountId: string) =>
    api<null>(`/v1/brands/${id}/social-accounts/${accountId}`, { method: "DELETE" }),

  // Templates (v0.3.4) — reusable post skeletons per brand.
  listTemplates: (id: string) =>
    api<{ templates: BrandPostTemplate[] }>(`/v1/brands/${id}/templates`, { method: "GET" }),
  getTemplate: (id: string, tid: string) =>
    api<BrandPostTemplate>(`/v1/brands/${id}/templates/${tid}`, { method: "GET" }),
  createTemplate: (id: string, body: BrandPostTemplateInput) =>
    api<BrandPostTemplate>(`/v1/brands/${id}/templates`, { method: "POST", body }),
  updateTemplate: (id: string, tid: string, body: BrandPostTemplateInput) =>
    api<BrandPostTemplate>(`/v1/brands/${id}/templates/${tid}`, { method: "PATCH", body }),
  deleteTemplate: (id: string, tid: string) =>
    api<null>(`/v1/brands/${id}/templates/${tid}`, { method: "DELETE" }),

  // Brand-kit auto-extract (v0.4.x) — one-click onboarding.
  // Upload an HTML brand kit (e.g. one generated by Claude) and the
  // server returns parsed palette + fonts + voice + logo + templates.
  // The dashboard then shows a review modal where the user picks which
  // pieces to apply. Nothing is persisted on the server side.
  extractKit: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFormData<ExtractedKit>(`/v1/brands/${id}/kit/extract`, fd);
  },
  // v0.5.17 — same endpoint, but XHR-backed so the dashboard can
  // track upload progress for the animated bar. onUploadProgress
  // fires 0..1 during the actual byte transfer; backend processing
  // (parse + ai-gateway refinement) happens AFTER the bytes land —
  // that phase is animated as indeterminate by the caller.
  extractKitWithProgress: (
    id: string,
    file: File,
    onUploadProgress: (fraction: number) => void,
  ): Promise<ExtractedKit> => {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE_URL}/v1/brands/${id}/kit/extract`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) {
          onUploadProgress(e.loaded / e.total);
        }
      };
      xhr.upload.onload = () => onUploadProgress(1);
      xhr.onload = () => {
        const text = xhr.responseText;
        const json = text ? safeParse(text) : null;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(json as ExtractedKit);
          return;
        }
        // Backend (v0.5.17+) returns {error, code, max_bytes} on the
        // failure paths the dashboard cares about (FILE_TOO_BIG,
        // NO_FILE, READ_FAILED). Earlier versions return a plain
        // {error: string}. Both go through the same ApiError so the
        // caller can branch on .body.code when present.
        const msg = isErrorBody(json) ? json.error : `HTTP ${xhr.status}`;
        reject(new ApiError(xhr.status, json, msg));
      };
      xhr.onerror = () => reject(new ApiError(0, null, "network error"));
      xhr.send(fd);
    });
  },

  // AI onboarding (v0.5.4) — operator describes their business in one
  // paragraph; Claude generates draft values for voice, audience,
  // products, services, topics. Same review-then-apply pattern as
  // extractKit; nothing is persisted server-side.
  onboardAI: (id: string, body: { description: string; brand_name?: string; country?: string; language?: string }) =>
    api<OnboardSuggestion>(`/v1/brands/${id}/ai-onboard`, { method: "POST", body }),
};

export type OnboardSuggestion = {
  industry?: string;
  category?: string;
  voice_summary?: string;
  voice_attributes?: Array<{ attribute: string; value: number }>;
  demographics?: Record<string, unknown>;
  psychographics?: Record<string, unknown>;
  products?: Array<{ name: string; description?: string; category?: string; features?: string[] }>;
  services?: Array<{ name: string; description?: string; target_audience?: string; price_hint?: string }>;
  topics_include?: string[];
  topics_avoid?: string[];
  warnings?: string[];
};

/** Result of POST /v1/brands/{id}/kit/extract. */
export type ExtractedLogo = {
  data_uri?: string;
  filename?: string;
  mime?: string;
  /** Human label (e.g. "Primary — Dark", "Reversed — Red"). */
  label?: string;
  /** Inferred surface treatment: dark | darker | light | red | transparent. */
  context?: string;
  /** "mark" (icon only) | "lockup" (mark + wordmark) | "wordmark". */
  form?: string;
};

export type ExtractedKit = {
  palette: Array<{ hex: string; name?: string; role?: string }>;
  typography: { display_font?: string; body_font?: string };
  voice_summary?: string;
  /** Primary variant — server-picked best default. Backward-compat. */
  logo?: ExtractedLogo | null;
  /** ALL canonical variants found in the kit (multi-variant brand kits). */
  logos?: ExtractedLogo[];
  templates: Array<{
    name: string;
    platform?: string;
    body: string;
  }>;
  warnings?: string[];
  /** v0.5.21 — true when the source HTML is a JS-rendered app
   *  (palette extracted but no logos / templates / voice). Dashboard
   *  surfaces a specific "Save Page As → Webpage Complete" hint. */
  js_rendered?: boolean;
};

// ---------- healthcheck (read-only status board) ----------

export type HealthcheckSeverity = "info" | "warning" | "critical";

export type HealthcheckCheck = {
  check: string;
  description: string;
  severity: HealthcheckSeverity;
  ok: boolean;
  http_status: number;
  latency_ms: number;
  error?: string;
  timestamp: string;
};

export type HealthcheckStatus = {
  ok: boolean;
  total: number;
  failed: number;
  critical_failed: number;
  checks: HealthcheckCheck[];
};

export type HealthcheckSummary = {
  ok: boolean;
  total: number;
  failed: number;
  critical_failed: number;
};

export const healthcheck = {
  status: () => api<HealthcheckStatus>("/v1/healthcheck/status", { method: "GET" }),
  summary: () => api<HealthcheckSummary>("/v1/healthcheck/status/summary", { method: "GET" }),
};

// ---------- geo proxy (brand-service v0.3.2+) ----------

export type GeoResult = {
  value: string;     // canonical id, e.g. "TR/İstanbul" or "TR/İstanbul/Beyoğlu"
  label: string;     // human display, e.g. "İstanbul, Türkiye"
  country?: string;  // ISO-3166 alpha-2
  admin1?: string;
  city?: string;
  lat?: string;
  lon?: string;
};

export const geocode = {
  search: (q: string, opts: { cc?: string } = {}) => {
    const params = new URLSearchParams({ q });
    if (opts.cc) params.set("cc", opts.cc);
    return api<{ results: GeoResult[] }>(
      `/v1/geo/search?${params.toString()}`,
      { method: "GET" },
    );
  },
};

// ---------- content-service: generations + posts ----------

export type Platform = "facebook" | "instagram" | "linkedin" | "telegram" | "x";
export type ContentType = "text" | "image";

export type GenerationStatus =
  | "pending"
  | "running"
  | "ready"
  | "failed"
  | "approved"
  | "rejected"
  | "abandoned";

export type Generation = {
  id: string;
  brand_id: string;
  initiated_by: string;
  mode: "single" | "bulk" | "campaign" | "regen";
  prompt: string;
  target_platforms: Platform[];
  content_types: ContentType[];
  reference_asset_ids: string[];
  scheduled_for?: string | null;
  status: GenerationStatus;
  idempotency_key?: string | null;
  created_at: string;
  updated_at: string;
};

export type VersionStatus = "pending" | "accepted" | "rejected";

export type GenerationVersion = {
  id: string;
  version_number: number;
  platform: Platform;
  provider: string; // "anthropic" | "openai" | …
  status: VersionStatus;
  text_body?: string | null;
  text_lang?: string | null;
  hashtags: string[];
  image_asset_id?: string | null;
  image_url?: string | null;
  text_model?: string | null;
  image_model?: string | null;
  cost_cents: number;
  latency_ms?: number | null;
  is_current: boolean;
  created_at: string;
  /** JSON-encoded prompt debug blob — content-service v0.2.18+.
   *  Shape: {"text_system":"...","text_user":"...","image":"..."}.
   *  Pre-v0.2.18 versions store the raw user prompt as a plain string. */
  prompt_used?: string | null;
};

export type GenerationResponse = {
  generation: Generation;
  versions: GenerationVersion[];
};

export type PostStatus =
  | "draft"
  | "scheduled"
  | "publishing"
  | "published"
  | "failed"
  | "cancelled";

export type Post = {
  id: string;
  brand_id: string;
  generation_id: string;
  platform: Platform;
  text_body: string;
  hashtags: string[];
  image_url?: string | null;
  ai_disclosure: boolean;
  status: PostStatus;
  scheduled_for?: string | null;
  published_at?: string | null;
  platform_post_id?: string | null;
  platform_permalink?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  cancelled_at?: string | null;
  failure_reason?: string | null;
  created_at: string;
  updated_at: string;
};

export const generations = {
  create: (body: {
    brand_id: string;
    prompt: string;
    target_platforms: Platform[];
    content_types?: ContentType[];
    idempotency_key?: string;
    // Multi-model fan-out (v0.2.0+):
    providers?: string[]; // empty/omitted = registry default
    variants_per_platform?: number; // 1..4, default 1
    // v0.2.1 — brand_asset IDs attached as image inputs (max 5).
    reference_asset_ids?: string[];
    // v0.2.48 — story support. 'feed' (default) or 'story'. Story
    // mode requires target_platforms ⊆ {'instagram','facebook'};
    // backend returns 400 otherwise. Backend also forces
    // content_types=['image'] for stories.
    post_type?: "feed" | "story";
  }) => api<GenerationResponse>("/v1/generations", { method: "POST", body }),
  get: (id: string) => api<GenerationResponse>(`/v1/generations/${id}`, { method: "GET" }),
  acceptVersion: (versionId: string) =>
    api<GenerationVersion>(`/v1/versions/${versionId}/accept`, { method: "POST" }),
  rejectVersion: (versionId: string) =>
    api<GenerationVersion>(`/v1/versions/${versionId}/reject`, { method: "POST" }),
  /** v0.2.7 — edit a pending variant's text/hashtags inline. */
  editVersion: (
    versionId: string,
    body: { text_body?: string; hashtags?: string[] },
  ) => api<GenerationVersion>(`/v1/versions/${versionId}`, { method: "PATCH", body }),
  /** v0.2.7 — swap the variant's image for one from the brand's asset library.
   *  Pass asset_id="" to clear. */
  replaceVersionImage: (versionId: string, assetId: string) =>
    api<GenerationVersion>(`/v1/versions/${versionId}/replace-image`, {
      method: "POST",
      body: { asset_id: assetId },
    }),
  /** v0.2.7 — ask the AI for another attempt on one platform.
   *  `notes` is the user's feedback prepended to the system prompt. */
  regenerate: (
    generationId: string,
    body: { platform: Platform; notes?: string; scope?: "text" | "image" | "both" },
  ) => api<GenerationVersion>(`/v1/generations/${generationId}/regenerate`, {
    method: "POST",
    body,
  }),
  approve: (
    id: string,
    body: {
      selections: Record<string, string>;
      ai_disclosure?: boolean;
      // v0.2.49 — RFC3339 timestamp from the schedule modal. When
      // set, every approved post for this generation lands as
      // 'scheduled' at this time. Omit to fall back to inherited
      // g.scheduled_for (bulk path) or 'draft' (legacy).
      scheduled_for?: string;
      // v0.2.49 — explicit opt-in for "decide later". When true,
      // posts land as 'draft' regardless of scheduled_for.
      save_as_draft?: boolean;
    },
  ) => api<{ posts: Post[] }>(`/v1/generations/${id}/approve`, { method: "POST", body }),
  listForBrand: (brandId: string) =>
    api<{ generations: Generation[] }>(`/v1/brands/${brandId}/generations`, { method: "GET" }),
};

// ---------- Bulk generation (v0.2.6 — docs/05-api §5.2) ----------

export type BulkJobChild = {
  generation_id: string;
  platform: string;
  status: string;
  scheduled_for?: string | null;
  created_at: string;
};

export type BulkJobChildStats = {
  total: number;
  pending: number;
  running: number;
  ready: number;
  approved: number;
  failed: number;
};

export type BulkJob = {
  id: string;
  workspace_id: string;
  brand_id: string;
  start_date: string;
  end_date: string;
  platforms: Platform[];
  count_per_platform: Record<string, number>;
  include_holidays: boolean;
  /** Operator-supplied topic — base prompt all children share. Added v0.2.35. */
  prompt?: string | null;
  status: "pending" | "running" | "completed" | "partial" | "failed";
  created_by: string;
  created_at: string;
  completed_at?: string | null;
  children?: BulkJobChild[];
  child_stats: BulkJobChildStats;
};

export const bulkGenerations = {
  /** Create a bulk job. Spreads `count_per_platform[p]` posts per platform
   *  evenly across [start_date, end_date]. Stub generations land in
   *  `pending` status; the in-cluster bulk worker (content-service v0.2.35+)
   *  picks them up and runs the AI fan-out.
   *
   *  `prompt` is the operator-supplied topic — e.g. "summer sale launch",
   *  "behind-the-scenes team series". Each child generation prepends this
   *  to its platform+date suffix so the bulk shares a coherent theme.
   *  Optional; empty falls back to the platform+date auto-prompt. */
  create: (
    brandId: string,
    body: {
      start_date: string;
      end_date: string;
      platforms: Platform[];
      count_per_platform: Record<string, number>;
      include_holidays?: boolean;
      prompt?: string;
      // v0.2.48 — story support. When 'story', platforms must be a
      // subset of {'instagram','facebook'} or the backend returns 400.
      post_type?: "feed" | "story";
    },
  ) => api<BulkJob>(`/v1/brands/${brandId}/bulk-generations`, { method: "POST", body }),
  get: (id: string) =>
    api<BulkJob>(`/v1/bulk-jobs/${id}`, { method: "GET" }),
  listForBrand: (brandId: string, limit?: number) =>
    api<{ bulk_jobs: BulkJob[] }>(
      `/v1/brands/${brandId}/bulk-jobs${limit ? `?limit=${limit}` : ""}`,
      { method: "GET" },
    ),
};

export const posts = {
  listForBrand: (brandId: string, status?: PostStatus) =>
    api<{ posts: Post[] }>(
      `/v1/brands/${brandId}/posts${status ? `?status=${status}` : ""}`,
      { method: "GET" },
    ),
  get: (id: string) => api<Post>(`/v1/posts/${id}`, { method: "GET" }),
  patch: (
    id: string,
    body: Partial<{ text_body: string; hashtags: string[]; ai_disclosure: boolean }>,
  ) => api<Post>(`/v1/posts/${id}`, { method: "PATCH", body }),
  schedule: (id: string, scheduledFor: string) =>
    api<Post>(`/v1/posts/${id}/schedule`, { method: "POST", body: { scheduled_for: scheduledFor } }),
  cancel: (id: string) => api<Post>(`/v1/posts/${id}/cancel`, { method: "POST" }),
  /** v0.2.3 — publish a post to its brand's configured Telegram
   *  channel via the shared platform bot. Returns the updated Post with
   *  status="published" + platform_post_id + platform_permalink. Errors:
   *  - 400 if post.platform !== "telegram" (v0.1.x only ships TG)
   *  - 409 if post not in draft/scheduled
   *  - 422 bot lacks channel admin (terminal — fix integration)
   *  - 424 brand has no telegram_channel configured
   *  - 503 cluster missing TELEGRAM_BOT_TOKEN secret */
  publish: (id: string) =>
    api<Post>(`/v1/posts/${id}/publish`, { method: "POST" }),
  delete: (id: string) => api<null>(`/v1/posts/${id}`, { method: "DELETE" }),
};

// ---------- Comments / DM inbox (v0.4.0 — spec docs/05-api §9) ----------

export type CommentStatus =
  | "new"
  | "classifying"
  | "draft_pending"
  | "approved"
  | "sent"
  | "hidden"
  | "escalated";

export type Comment = {
  id: string;
  workspace_id: string;
  brand_id: string;
  post_id: string | null;
  platform: Platform | string;
  platform_comment_id: string;
  parent_platform_comment_id: string | null;
  author_handle: string | null;
  author_display_name: string | null;
  body: string;
  language: string | null;
  received_at: string;
  category: string | null;
  confidence: number | null;
  status: CommentStatus;
  hidden_at: string | null;
  escalated_at: string | null;
  webhook_event_id: string | null;
  created_at: string;
};

export type ReplyDraft = {
  id: string;
  comment_id: string;
  draft_text: string;
  draft_lang: string | null;
  drafted_by: string;
  ai_model: string | null;
  cost_cents: number;
  approved_by: string | null;
  approved_at: string | null;
  sent_at: string | null;
  platform_reply_id: string | null;
  created_at: string;
};

export const comments = {
  /** List comments for a brand, optionally filtered by status. The
   *  backend supports any of the CommentStatus values; omit for all. */
  listForBrand: (brandId: string, opts: { status?: CommentStatus; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.status) qs.set("status", opts.status);
    if (opts.limit) qs.set("limit", String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return api<{ comments: Comment[] }>(`/v1/brands/${brandId}/comments${suffix}`, { method: "GET" });
  },
  /** Full single-comment view with all drafts attached. */
  get: (id: string) =>
    api<{ comment: Comment; drafts: ReplyDraft[] }>(`/v1/comments/${id}`, { method: "GET" }),
  /** Ask the AI to draft a reply (or re-draft with extra guidance). */
  regenerateDraft: (id: string, body: { extra_instructions?: string } = {}) =>
    api<ReplyDraft>(`/v1/comments/${id}/replies`, { method: "POST", body }),
  hide: (id: string) =>
    api<Comment>(`/v1/comments/${id}/hide`, { method: "POST" }),
  escalate: (id: string) =>
    api<Comment>(`/v1/comments/${id}/escalate`, { method: "POST" }),
  /** Edit a draft's text before approving — same shape the user expects
   *  from any drafting tool. The backend mutates the existing row. */
  editDraft: (replyId: string, body: { draft_text: string }) =>
    api<ReplyDraft>(`/v1/replies/${replyId}`, { method: "PATCH", body }),
  /** Approve a draft for sending. v0.1.x stores intent; publisher will
   *  pick it up once the dedicated service ships. */
  approveDraft: (replyId: string) =>
    api<ReplyDraft>(`/v1/replies/${replyId}/approve`, { method: "POST" }),
};

export type WorkspaceMemberRole = "owner" | "admin" | "editor" | "approver" | "viewer";

export type WorkspaceMember = {
  user_id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  role: WorkspaceMemberRole;
  status: string;
  joined_at: string;
};

// AddMemberInvite is what /v1/workspaces/{id}/members returns when
// the email belongs to an unregistered user — we send an invitation
// email rather than 404. Discriminate by `kind === "invited"`.
export type AddMemberInvite = {
  kind: "invited";
  email: string;
  role: WorkspaceMemberRole;
  expires_at: string;
  workspace_id: string;
  duplicate?: boolean; // true when an invitation was already pending
};

export const workspaces = {
  create: (body: { name: string; slug: string; timezone?: string; locale?: string }) =>
    api<Workspace>("/v1/workspaces", { method: "POST", body }),
  list: () => api<{ workspaces: Workspace[] }>("/v1/workspaces", { method: "GET" }),
  switch: (body: { workspace_id: string }) =>
    api<{ current_workspace_id: string }>("/v1/workspaces/switch", { method: "POST", body }),

  // v0.3.0 — per-workspace edit + member management.
  get: (id: string) =>
    api<Workspace>(`/v1/workspaces/${id}`, { method: "GET" }),
  update: (id: string, body: { name?: string; timezone?: string; locale?: string }) =>
    api<Workspace>(`/v1/workspaces/${id}`, { method: "PATCH", body }),

  listMembers: (id: string) =>
    api<{ members: WorkspaceMember[] }>(`/v1/workspaces/${id}/members`, { method: "GET" }),
  // addMember returns one of two shapes depending on whether the email
  // belongs to a registered user:
  //   - existing user: WorkspaceMember (HTTP 201)
  //   - unregistered email: AddMemberInvite (HTTP 202)
  // The dashboard branches on `kind` to decide whether to refresh the
  // members roster or show an "invitation sent" toast.
  addMember: (id: string, body: { email: string; role?: WorkspaceMemberRole }) =>
    api<WorkspaceMember | AddMemberInvite>(`/v1/workspaces/${id}/members`, { method: "POST", body }),
  updateMemberRole: (id: string, userId: string, body: { role: WorkspaceMemberRole }) =>
    api<WorkspaceMember>(`/v1/workspaces/${id}/members/${userId}`, { method: "PATCH", body }),
  removeMember: (id: string, userId: string) =>
    api<void>(`/v1/workspaces/${id}/members/${userId}`, { method: "DELETE" }),
  transferOwnership: (id: string, body: { new_owner_user_id: string }) =>
    api<WorkspaceMember>(`/v1/workspaces/${id}/transfer-ownership`, { method: "POST", body }),
  acceptInvitation: (token: string) =>
    api<{ workspace_id: string; workspace_name: string; role: WorkspaceMemberRole }>(
      "/v1/workspaces/accept-invite", { method: "POST", body: { token } }),
};
